import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $createTextNode,
  $getRoot,
  $insertNodes,
  $isElementNode,
  EditorState,
  ElementNode,
  LexicalEditor,
  SerializedEditorState,
  SerializedLexicalNode,
} from "lexical";
import { Attachment, RichContentDocument } from "src/types/messages/content";
import { SerializedImageNode } from "../nodes/ImageNode";
import { isVariableNode } from "../plugins/VariablesPlugin/utils";

/**
 * Checks whether the given Lexical editor state (string, EditorState, or SerializedEditorState)
 * represents an "empty" document, i.e., one with no meaningful content.
 *
 * @returns true if the document has no root children, or only one paragraph node with no children.
 */
export function isEmptyLexicalState(
  value: string | EditorState | SerializedEditorState | null | undefined
): boolean {
  if (!value) {
    return true;
  }
  const lexicalState =
    typeof value === "object"
      ? "toJSON" in value
        ? value.toJSON()
        : (value as SerializedEditorState)
      : JSON.parse(value);

  if (
    !lexicalState.root?.children ||
    lexicalState.root.children.length === 0 ||
    (lexicalState.root.children.length === 1 &&
      lexicalState.root.children[0]?.children?.length === 0)
  ) {
    return true;
  }

  return false;
}

/**
 * Converts a SerializedEditorState into plain text using the provided LexicalEditor.
 *
 * @param editor - The LexicalEditor instance to use for parsing the SerializedEditorState.
 * @param input - The SerializedEditorState to convert to plain text.
 * @param expandedMustache - When true, variable nodes export expanded text; when false, mustache syntax. Default true.
 * @returns The plain text extracted from the SerializedEditorState.
 */
export function serializedEditorStateToPlainText(
  editor: LexicalEditor,
  input: SerializedEditorState | undefined | null,
  expandedMustache: boolean = true
): string | undefined {
  if (!input) return undefined;
  return editorStateToPlainText(
    editor.parseEditorState(input),
    expandedMustache
  );
}

/**
 * Converts a Lexical EditorState to plain text.
 *
 * Accepts either a LexicalEditor instance or an EditorState.
 * If a LexicalEditor is provided, retrieves its EditorState.
 * Traverses the root node to extract all text content as a string.
 *
 * @param input - A LexicalEditor or EditorState to extract plain text from.
 * @param expandedMustache - When true, variable nodes export expanded text; when false, mustache syntax. Default true.
 * @returns The plain text extracted from the editor state.
 */
export function editorStateToPlainText(
  input: LexicalEditor | EditorState,
  expandedMustache: boolean = true
): string {
  const editorState =
    "getEditorState" in input ? input.getEditorState() : input;
  let plainText = "";

  editorState.read(() => {
    const root = $getRoot();
    plainText = root ? getPlainText(root, expandedMustache) : "";
  });

  return plainText;
}

export const DOUBLE_LINE_BREAK = "\n\n";

/**
 * Extracts plain text content from a Lexical root node.
 *
 * Traverses the node's children, handling variable nodes and element nodes appropriately.
 * Adds double line breaks between block-level elements to preserve paragraph structure.
 *
 * @param rootNode - The root node to extract plain text from.
 * @param expandedMustache - When true, variable nodes export expanded text; when false, mustache syntax. Default true.
 * @returns The plain text extracted from the root node.
 */
export function getPlainText(
  rootNode: ElementNode,
  expandedMustache: boolean = true
): string {
  let textContent = "";
  const children = rootNode.getChildren();
  const childrenLength = children.length;
  for (let i = 0; i < childrenLength; i++) {
    const child = children[i];
    if (isVariableNode(child)) {
      textContent += child.getPlainText(expandedMustache);
    } else if ($isElementNode(child)) {
      textContent += getPlainText(child, expandedMustache);
    } else {
      textContent += child?.getTextContent() ?? "";
    }

    if (
      $isElementNode(child) &&
      i !== childrenLength - 1 &&
      !child.isInline()
    ) {
      textContent += DOUBLE_LINE_BREAK;
    }
  }
  return textContent;
}

/**
 * Converts a Lexical EditorState to HTML.
 *
 * Generates HTML from the root node's children using the editor's registered nodes.
 *
 * @param editor - The LexicalEditor instance to use for HTML generation.
 * @param editorState - Optional EditorState; if omitted, uses editor.getEditorState().
 * @returns The HTML string generated from the editor state.
 */
export function editorStateToHtml(
  editor: LexicalEditor,
  editorState?: EditorState
): string {
  let html = "";
  const state = editorState ?? editor.getEditorState();
  state.read(() => {
    html = $generateHtmlFromNodes(editor, null);
  });
  return html;
}

/**
 * Converts a SerializedEditorState (JSON) to HTML string.
 * Requires a LexicalEditor instance to access registered nodes and their exportDOM methods.
 *
 * @param editor - The LexicalEditor instance (needed for node registration and exportDOM)
 * @param serializedState - The SerializedEditorState JSON object to convert
 * @returns HTML string representation of the editor state
 */
export function serializedEditorStateToHtml(
  editor: LexicalEditor,
  serializedState: SerializedEditorState | undefined | null
): string {
  if (!serializedState) {
    return "";
  }

  let html = "";
  const parsedEditorState = editor.parseEditorState(serializedState);
  parsedEditorState.read(() => {
    html = $generateHtmlFromNodes(editor, null);
  });
  return html;
}

/**
 * Converts a SerializedEditorState to HTML string with inline images replaced with CIDs.
 *
 * @param editor - The LexicalEditor instance to use for HTML generation.
 * @param serializedState - The SerializedEditorState to convert to HTML.
 * @returns The HTML string with inline images replaced with CIDs.
 */
export function serializedEditorStateToHtmlWithCIDs(
  editor: LexicalEditor,
  serializedState: SerializedEditorState | undefined | null
): HtmlWithAttachments {
  const html = serializedEditorStateToHtml(editor, serializedState);
  return replaceInlineImagesWithCID(html);
}

/**
 * Imports an HTML string into a Lexical editor, replacing its current content.
 *
 * @param editor - The LexicalEditor instance to import the HTML into.
 * @param html - The HTML string to import.
 */
export function importHtml(
  editor: LexicalEditor,
  html: string,
  discrete?: true
): void {
  editor.update(
    () => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(html, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      $getRoot().clear();
      $insertNodes(nodes);
    },
    { discrete }
  );
}

/**
 * Converts plain text to HTML by:
 * 1. Escaping special HTML characters (&, <, >, ", ') to prevent XSS
 * 2. Converting newlines to <br> tags
 * 3. Wrapping the entire text in a paragraph tag
 *
 * @param plainText - The plain text string to convert to HTML
 * @returns An HTML string with escaped characters and proper formatting
 *
 * @example
 * const html = convertPlainTextToHtml("Hello & goodbye\nNew line");
 * // Returns: "<p>Hello &amp; goodbye<br>New line</p>"
 */
export function convertPlainTextToHtml(plainText: string): string {
  const sanitized = plainText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\n/g, "<br>");
  return `<p>${sanitized}</p>`;
}

export interface HtmlWithAttachments {
  html: string;
  attachments: Attachment[];
}

/**
 * Replaces inline images with CIDs in an HTML string.
 */
export function replaceInlineImagesWithCID(html: string): HtmlWithAttachments {
  // Regex to match <img> tags with inline data URIs in the src attribute
  const imgRegex =
    /<img([^>]*?)src=["'](data:([^;]+);base64,([^"']+))["']([^>]*?)>/gi;

  // Array to store the metadata for attachments
  const attachments: Attachment[] = [];

  // Replace all matches in the HTML
  const updatedHtml = html.replaceAll(
    imgRegex,
    (match, beforeSrc, dataUri, mimeType, base64Data, afterSrc) => {
      // Generate a unique CID
      const cid = `image_${attachments.length + 1}`;

      // Add the attachment metadata
      attachments.push({
        data: base64Data,
        cid,
        mimeType,
        filename: `image-${attachments.length + 1}.${mimeType.split("/")[1]}`,
      });

      // Return the new <img> tag with the CID in the src attribute
      return `<img${beforeSrc}src="cid:${cid}"${afterSrc}>`;
    }
  );

  return { html: updatedHtml, attachments };
}

/**
 * Converts a Lexical EditorState to HTML string with inline images replaced with CIDs.
 *
 * @param editor - The LexicalEditor instance to use for HTML generation.
 * @returns The HTML string with inline images replaced with CIDs.
 */
export function editorStateToHtmlWithCIDs(
  editor: LexicalEditor
): HtmlWithAttachments | null {
  const html = editorStateToHtml(editor);
  return replaceInlineImagesWithCID(html);
}

/**
 * Replaces CIDs with inline images in an HTML string.
 *
 * @param html - The HTML string to replace CIDs with inline images.
 * @param attachments - The attachments to use for replacing the CIDs with inline images.
 * @returns The HTML string with CIDs replaced with inline images.
 */
export function replaceCIDsWithInlineImages(
  html: string,
  attachments: Attachment[] = []
): string {
  const imgRegex = /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*?)>/gi;

  return html.replaceAll(imgRegex, (match, beforeSrc, cid, afterSrc) => {
    const attachment = attachments.find((attachment) => attachment.cid === cid);
    if (attachment) {
      return `<img${beforeSrc}src="data:${attachment.mimeType};base64,${attachment.data}"${afterSrc}>`;
    }
    return match;
  });
}

/**
 * Inserts plain text into a Lexical editor.
 *
 * @param editor - The LexicalEditor instance to use for inserting the plain text.
 * @param text - The plain text string to insert.
 * @param discrete - If true, the update is applied synchronously.
 */
export function importPlainText(
  editor: LexicalEditor,
  text: string,
  discrete?: true
): void {
  editor.update(
    () => {
      const root = $getRoot();
      root.clear(); // Clear existing content
      $insertNodes([$createTextNode(text)]); // Append new text
    },
    { discrete }
  );
}

/**
 * Parses a value into SerializedEditorState (string → parsed JSON, object → as-is).
 */
export function parseSerializedEditorState(
  value: string | SerializedEditorState | undefined | null
): SerializedEditorState | undefined {
  if (!value) return undefined;
  if (typeof value === "string") {
    return JSON.parse(value) as SerializedEditorState;
  }
  return value as SerializedEditorState;
}

/**
 * Merges two serialized editor states: appends the second's root children after a horizontal rule.
 */
export function mergeSerializedEditorStates(
  baseState: SerializedEditorState | undefined | null,
  appendState: SerializedEditorState | undefined | null
): SerializedEditorState | undefined {
  if (!baseState) return appendState ?? undefined;
  if (!appendState) return baseState;
  const mergedState = JSON.parse(
    JSON.stringify(baseState)
  ) as SerializedEditorState;
  if (mergedState.root?.children && appendState.root?.children) {
    mergedState.root.children = [
      ...mergedState.root.children,
      { type: "horizontalrule" },
      ...JSON.parse(JSON.stringify(appendState.root.children)),
    ];
  } else if (appendState.root?.children) {
    mergedState.root.children = JSON.parse(
      JSON.stringify(appendState.root.children)
    );
  }
  return mergedState;
}

function restoreNodeFromCIDs(
  node: SerializedLexicalNode,
  attachments: Attachment[]
): void {
  if (node.type === "image") {
    const imageNode = node as SerializedImageNode;
    const src = imageNode.src;
    if (src?.startsWith("cid:")) {
      const cid = src.replace("cid:", "");
      const attachment = attachments.find((att) => att.cid === cid);
      if (attachment) {
        imageNode.src = `data:${attachment.mimeType};base64,${attachment.data}`;
      }
    }
  }
  if ("children" in node && Array.isArray(node.children)) {
    for (const child of node.children) {
      restoreNodeFromCIDs(child, attachments);
    }
  }
}

/**
 * Replaces cid: image sources in a serialized editor state with inline data URIs from attachments.
 */
export function restoreCIDsInSerializedState(
  serializedState: SerializedEditorState | undefined | null,
  attachments: Attachment[]
): SerializedEditorState | undefined {
  if (!serializedState) return undefined;
  const clonedState = JSON.parse(
    JSON.stringify(serializedState)
  ) as SerializedEditorState;
  if (clonedState.root?.children) {
    for (const child of clonedState.root.children) {
      restoreNodeFromCIDs(child, attachments);
    }
  }
  return clonedState;
}

/**
 * Gets the non-inline attachment CIDs from a RichContentDocument.
 *
 * @param richContent - The RichContentDocument to get the non-inline attachment CIDs from.
 * @returns The non-inline attachment CIDs.
 */
export function getNonInlineAttachmentCids(
  richContent: RichContentDocument | null
): Attachment[] | undefined {
  if (!richContent) return [];
  const imgRegex = /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*?)>/gi;
  const matches = richContent.html?.matchAll(imgRegex);
  const cids = Array.from(matches ?? []).map(([, , cid]) => cid);
  return richContent.attachments?.filter(
    (attachment) => !cids.includes(attachment.cid)
  );
}

/**
 * Converts files to attachments.
 *
 * @param files - The files to convert to attachments.
 * @returns The attachments.
 */
export async function convertFilesToAttachments(
  files: File[]
): Promise<Attachment[]> {
  return await Promise.all(
    files.map(async (file, idx) => {
      const data = await convertFileToBase64(file);
      return {
        data,
        cid: `file_${idx + 1}`,
        mimeType: file.type,
        filename: file.name,
      };
    })
  );
}

/**
 * Converts a file to a base64 string.
 *
 * @param file - The file to convert to a base64 string.
 * @returns The base64 string.
 */
function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      resolve(base64 as string);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Converts an attachment to a File object.
 *
 * @param attachment - The attachment to convert to a File object.
 * @returns The File object.
 */
export async function convertAttachmentToFile(
  attachment: Attachment
): Promise<File> {
  const dataUrl = `data:${attachment.mimeType};base64,${attachment.data}`;
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], attachment.filename, { type: attachment.mimeType });
}
